import OT from '@opentok/client';
import throttle from 'lodash.throttle';
import { v4 as uuid } from 'uuid';

import {
  INIT_PUBLISHER,
  SUCCESS_INIT_PUBLISHER,
  ERR_INIT_PUBLISHER,
  DESTROY_PUBLISHER,
  ERR_PUBLISH_PUBLISHER,
  UPDATE_PUBLISHER,
  UPDATE_PUBLISHER_AUDIO_LEVEL,
} from 'OPENTOK/action_types';

import { createStream, destroyStream } from 'OPENTOK/actions/stream';

import { PUBLISHER_STATUS_CONNECTING, PUBLISHER_STATUS_CONNECTED } from 'OPENTOK/models/publisher';

const initPublisherAction = (id, videoType, object) => ({
  type: INIT_PUBLISHER,
  id,
  payload: {
    object,
    videoType,
  },
});

const successInitPublisher = (id) => ({
  type: SUCCESS_INIT_PUBLISHER,
  id,
});

const errInitPublisher = (id, err) => ({
  type: ERR_INIT_PUBLISHER,
  id,
  err,
});

const updateName = (id, name) => ({ type: UPDATE_PUBLISHER, id, payload: { name } });
const updateVideoAudioStatus = (id, streamId, videoStatus, audioStatus) => ({
  type: UPDATE_PUBLISHER,
  id,
  payload: { streamId, videoStatus, audioStatus },
});
const updateAudioLevel = (id, audioLevel) => ({
  type: UPDATE_PUBLISHER_AUDIO_LEVEL,
  id,
  payload: { audioLevel },
});
const createPublisherVideoElement = (id, videoElement) => ({
  type: UPDATE_PUBLISHER,
  id,
  payload: {
    videoElement,
  },
});

const setSessionIdToPublisher = (id, sessionId) => ({
  type: UPDATE_PUBLISHER,
  id,
  payload: {
    sessionId,
  },
});

const errPublishPublisher = (id) => ({ type: ERR_PUBLISH_PUBLISHER, id });
const updatePublisherStatus = (id, status) => ({
  type: UPDATE_PUBLISHER,
  id,
  payload: {
    status,
  },
});

const eventHandlers = (id, dispatch) => ({
  accessDenied: () => {},
  destroyed: () => {
    dispatch(destroyPublisher(id));
  },
  mediaStopped: () => {},

  streamCreated: (event) => {
    const stream = event.stream;
    dispatch(updateName(id, stream.name));
    dispatch(updateVideoAudioStatus(id, stream.streamId, stream.hasVideo, stream.hasAudio));
    dispatch(createStream(stream.streamId, stream, stream.hasAudio, stream.hasVideo));
  },
  streamDestroyed: (event) => {
    const stream = event.stream;
    dispatch(updateVideoAudioStatus(id, null, null, null));
    dispatch(destroyStream(stream.streamId));
  },
  videoDimensionsChanged: () => {},
  videoElementCreated: (event) => {
    dispatch(createPublisherVideoElement(id, event.element));
  },
  audioLevelUpdated: throttle((event) => {
    dispatch(updateAudioLevel(id, event.audioLevel));
  }, 100),
});

export const initPublisher = (
  videoType,
  properties = {},
  targetElement = null,
  optEventHandlers = null,
) => (dispatch, _) =>
  new Promise((resolve, reject) => {
    const publisherId = uuid();

    const publisher = OT.initPublisher(targetElement, properties, (err) => {
      if (err) {
        dispatch(errInitPublisher(publisherId, err));
        reject({ publisherId, err });
      } else {
        dispatch(successInitPublisher(publisherId));
        resolve(publisherId);
      }
    });

    publisher.on(eventHandlers(publisherId, dispatch));
    if (optEventHandlers) {
      publisher.on(optEventHandlers(publisherId, dispatch));
    }

    dispatch(initPublisherAction(publisherId, videoType, publisher));
  });

export const publishPublisherToSession = (publisherId, sessionId) => (dispatch, getState) => {
  const publisher = getState().getIn(['ot', 'publishers', publisherId]);
  const session = getState().getIn(['ot', 'sessions', sessionId]);
  const sessionObject = session.get('object');
  const publisherObject = publisher.get('object');

  return new Promise((resolve, reject) => {
    dispatch(setSessionIdToPublisher(publisherId, sessionId));
    dispatch(updatePublisherStatus(publisherId, PUBLISHER_STATUS_CONNECTING));

    sessionObject.publish(publisherObject, (err) => {
      if (err) {
        dispatch(errPublishPublisher(publisherId));
        reject({ err });
      } else {
        dispatch(updatePublisherStatus(publisherId, PUBLISHER_STATUS_CONNECTED));
        resolve();
      }
    });
  });
};

const unpublishPublisher = (publisherId, getState) => {
  const publisher = getState().getIn(['ot', 'publishers', publisherId]);

  if (publisher && publisher.get('sessionId')) {
    const sessionObject = getState().getIn([
      'ot',
      'sessions',
      publisher.get('sessionId'),
      'object',
    ]);
    sessionObject.unpublish(publisher.get('object'));
  }
};

export const destroyPublisher = (publisherId) => (dispatch, getState) => {
  unpublishPublisher(publisherId, getState);

  dispatch({ type: DESTROY_PUBLISHER, id: publisherId });
};

export const initAndPublihshPublisher = (
  videoType,
  sessionId,
  properties = {},
  targetElement = null,
  optEventHandlers = null,
) => (dispatch, getState) => {
  const session = getState().getIn(['ot', 'sessions', sessionId]);
  if (!session.isEnablePublish()) {
    console.error('今publishできません。');
    return;
  }

  return dispatch(initPublisher(videoType, properties, targetElement, optEventHandlers)).then((id) =>
    dispatch(publishPublisherToSession(id, sessionId)),
  );
};

export const publishCamera = (
  sessionId,
  properties = {},
  targetElement = null,
  optEventHandlers = null,
) => (dispatch, _) =>
  dispatch(
    initAndPublihshPublisher('camera', sessionId, properties, targetElement, optEventHandlers),
  );

export const togglePublisherMedia = (id, mediaType) => (dispatch, getState) => {
  const publisher = getState().getIn(['ot', 'publishers', id]);
  const publisherObject = getState().getIn(['ot', 'publishers', id, 'object']);
  if (!publisherObject) {
    return;
  }

  if (mediaType === 'audio') {
    const value = !publisher.get('audioStatus');
    publisherObject.publishAudio(value);
  } else if (mediaType === 'video') {
    const value = !publisher.get('videoStatus');
    publisherObject.publishVideo(value);
  }
};
